import FormControl from '@mui/material/FormControl';
import { DatePicker as MuiDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { csCZ, deDE, enUS, fiFI, nbNO, svSE } from '@mui/x-date-pickers/locales';
import { isValid, startOfDay } from 'date-fns';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDateInputFormat, getDateLocale } from '../../utils/formatDatetime/formatDatetime';
import { FormHelperText } from '../FormHelperText/FormHelperText';
import { FormLabel } from '../FormLabel/FormLabel';
import { useColors } from '../ThemeProvider';

interface Props {
  id?: string;
  label: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  disabled?: boolean;
  required?: boolean;
  errorText?: string;
  helperText?: string;
  overrideVisited?: boolean;
  inputWidth?: '10rem' | '15rem' | '20rem' | '100%';
  maxDate?: Date;
  minDate?: Date;
  shouldDisableDate?: (date: Date) => boolean;
}

const pickerLocales: Record<string, typeof enUS> = {
  en: enUS,
  nb: nbNO,
  de: deDE,
  sv: svSE,
  fi: fiFI,
  cs: csCZ,
};

export function DatePicker({
  id,
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  errorText = '',
  helperText = '',
  overrideVisited = false,
  inputWidth = '100%',
  maxDate,
  minDate,
  shouldDisableDate,
}: Props) {
  useTranslation();
  const locale = getDateLocale();
  const pickerLocale = pickerLocales[locale.code.split('-')[0]] ?? enUS;

  const generatedId = useId();
  const { primary, textPrimary } = useColors();

  const [visited, setVisited] = useState(false);

  const datePickerId = id ?? generatedId;
  const labelId = `${datePickerId}-label`;
  const helperTextId = `${datePickerId}-helper-text`;
  const maxWidth = inputWidth === '10rem' || inputWidth === '15rem' ? '20rem' : inputWidth;
  const showError = !!errorText && (visited || overrideVisited);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={locale}
      localeText={pickerLocale.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <FormControl fullWidth sx={{ maxWidth }}>
        <FormLabel
          componentId={datePickerId}
          id={labelId}
          label={label}
          required={required}
          valid={errorText.length === 0}
        />

        <MuiDatePicker
          disabled={disabled}
          format={getDateInputFormat()}
          value={value}
          maxDate={maxDate || undefined}
          minDate={minDate || undefined}
          onChange={(value: Date | null) => {
            const normalizedValue = value ? startOfDay(value) : null;
            onChange(isValid(normalizedValue) ? normalizedValue : null);
            setVisited(true);
          }}
          shouldDisableDate={shouldDisableDate}
          slotProps={{
            openPickerButton: {
              id: `${id ?? generatedId}-open-picker`,
            },
            textField: {
              error: showError,
              id: datePickerId,
              name: datePickerId,
              inputProps: {
                'aria-describedby': helperTextId,
                'aria-labelledby': labelId,
              },
              onBlur: () => setVisited(true),
              sx: {
                width: inputWidth,
                maxWidth: '100%',
                whiteSpace: 'pre-line',
                svg: { color: primary },
                input: {
                  color: textPrimary,
                  '&::placeholder': {
                    color: textPrimary,
                    opacity: 0.7,
                  },
                },
                '& .MuiPickersInputBase-root': {
                  padding: '3.5px 12px',
                },
                '& .MuiPickersSectionList-root': {
                  padding: '0',
                },
              },
            },
          }}
        />

        <FormHelperText
          id={helperTextId}
          showError={showError}
          text={showError ? errorText : helperText}
        />
      </FormControl>
    </LocalizationProvider>
  );
}
